import { maxBy, uniqWith } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import {
  Load,
  LoadCase,
  LoadDefinitionWrapper,
  LoadDirection,
  LoadMemberType,
  loadMemberTypes,
  LoadWrapper,
  ManualLoadSettings,
  MemberType,
} from '@paragon/contract-library';
import { Direction2D, Line2D, LineSegment2D, point2DEquals } from '@paragon/geometry-library';
import { calculateMemberMidline } from '@paragon/three-shared';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { LoadVisualLoad } from '../../library/loadProjectionUtilities';
import { RootState } from '..';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getAnalogModelForActiveComponentDesign } from './getAnalogModelForActiveComponentDesignSelector';
import { getLoadCasesForActiveComponentDesign } from './getLoadCasesForActiveComponentDesignSelector';

function getUserLoads(allLoadCases: readonly LoadCase[]): readonly Load[] {
  const pointLoadsByLoadCase = allLoadCases
    .filter((loadCase) => !loadCase.name.toLowerCase().includes('moving'))
    .map((loadCase) => loadCase.loads.filter((load) => load.distribution === 'Concentrated'));

  const pointLoadApplications = uniqWith(
    pointLoadsByLoadCase.flat().map(({ start, direction }) => ({
      location: start,
      direction: Direction2D.fromObject(direction),
    })),
    (l1, l2) => point2DEquals(l1.location, l2.location) && l1.direction.equals(l2.direction),
  );

  const groupToShowAtEachApplication = pointLoadApplications.map((application) => {
    const loadsAtApplicationByLoadCase = pointLoadsByLoadCase.map((loads) =>
      loads.filter(
        (load) =>
          point2DEquals(application.location, load.start) &&
          application.direction.equals(Direction2D.fromObject(load.direction)),
      ),
    );

    return maxBy(loadsAtApplicationByLoadCase, (loads) => loads.reduce((sum, load) => sum + load.startMagnitude, 0))!;
  });

  return groupToShowAtEachApplication.flat();
}

interface ManualLoadSnap {
  readonly geometry: LineSegment2D;
  readonly axis: 'X' | 'Y';
  readonly memberType: LoadMemberType;
}

function manualLoadSettingToLoadVisualLoad(
  manualLoadSetting: ManualLoadSettings,
  snaps: readonly ManualLoadSnap[],
): LoadVisualLoad | null {
  switch (manualLoadSetting.load.loadInsertionMethod) {
    case 'ProjectedCoordinates':
      return loadDefinitionToLoadVisualLoad(manualLoadSetting.load, snaps);
    case 'Points':
      return loadToAnalysisLoad(manualLoadSetting.load);
    default:
      assertExhaustiveSwitch(manualLoadSetting.load);
  }
}

function loadDefinitionToLoadVisualLoad(
  { loadDefinition }: LoadDefinitionWrapper,
  snaps: readonly ManualLoadSnap[],
): LoadVisualLoad | null {
  if (loadDefinition.distribution !== 'Concentrated') return null;

  const loadDefinitionLine = Line2D.fromObject(
    loadDefinition.definitionAxis === 'X'
      ? { basePoint: { x: loadDefinition.coordinate, y: 0 }, direction: { x: 0, y: 1 } }
      : { basePoint: { x: 0, y: loadDefinition.coordinate }, direction: { x: 1, y: 0 } },
  );

  const intersectionPointsWithDirections = snaps
    .filter(({ memberType }) => memberType === loadDefinition.memberType)
    .map((snap) => ({ direction: snap.geometry.direction, point: snap.geometry.intersectWithLine(loadDefinitionLine) }))
    .map(({ direction, point }) => point && { direction, point })
    .filter(notNullish);

  const intersectionPoints = uniqWith(
    intersectionPointsWithDirections,
    ({ direction, point }, other) => direction.equals(other.direction) && point.equals(other.point),
  );

  if (intersectionPoints.length !== 1) return null;

  const { point, direction: snapDirection } = intersectionPoints[0];

  const direction = getLoadDirection(loadDefinition.loadDirection, snapDirection);

  return {
    guid: crypto.randomUUID(),
    distribution: 'Concentrated',
    loadType: loadDefinition.loadType,
    memberType: loadMemberTypeToMemberType(loadDefinition.memberType),
    start: point,
    end: null,
    direction,
    startMagnitude: loadDefinition.magnitude,
    endMagnitude: loadDefinition.magnitude,
    isGirderLoad: false,
    isManualLoad: true,
    isMovingLoad: false,
  };
}

function loadToAnalysisLoad({ load }: LoadWrapper): LoadVisualLoad | null {
  if (load.distribution !== 'Concentrated') return null;
  return {
    guid: crypto.randomUUID(),
    distribution: 'Concentrated',
    loadType: load.loadType,
    memberType: load.memberType,
    start: load.start,
    end: null,
    direction: load.direction,
    startMagnitude: load.startMagnitude,
    endMagnitude: load.endMagnitude,
    isGirderLoad: false,
    isManualLoad: true,
    isMovingLoad: false,
  };
}

function getLoadDirection(loadDirection: LoadDirection, linkDirection: Direction2D): Direction2D {
  switch (loadDirection) {
    case 'GlobalDown':
      return Direction2D.down;
    case 'GlobalUp':
      return Direction2D.up;
    case 'GlobalRight':
      return Direction2D.right;
    case 'GlobalLeft':
      return Direction2D.left;
    case 'NormalDown':
      return linkDirection.rotateReverseQuarterTurn();
    case 'NormalUp':
      return linkDirection.rotateQuarterTurn();
    case 'ParallelRight':
      return linkDirection;
    case 'ParallelLeft':
      return linkDirection.reverse();
    default:
      assertExhaustiveSwitch(loadDirection);
  }
}

function memberTypeToLoadMemberType(
  memberType: MemberType,
  geometry: LineSegment2D,
  span: number,
): LoadMemberType | null {
  // Some member type have a one-to-one correspondence.
  if ((loadMemberTypes as string[]).includes(memberType)) return memberType as LoadMemberType;

  // The only special case is end verticals, which are codified in LoadMemberType as either left or right. We use
  // the available geometry to decide which side of the component the end vertical is on.
  if (memberType === 'EndVertical') return geometry.midpoint.x < span / 2 ? 'LeftEndVertical' : 'RightEndVertical';

  // The member type was not convertible.
  return null;
}

function loadMemberTypeToMemberType(loadMemberType: LoadMemberType): MemberType {
  switch (loadMemberType) {
    case 'BottomChord':
    case 'TopChord':
    case 'Web':
      return loadMemberType;
    case 'LeftEndVertical':
    case 'RightEndVertical':
      return 'EndVertical';
    default:
      assertExhaustiveSwitch(loadMemberType);
  }
}

const getManualLoadSnapsFromAnalogModel = createSelector(
  getAnalogModelForActiveComponentDesign,
  getActiveComponentDesign,
  (analogModel, componentDesign): readonly ManualLoadSnap[] | null =>
    analogModel &&
    componentDesign &&
    analogModel.analogLinks
      .map(({ memberStack, iJoint, jJoint }): ManualLoadSnap | null => {
        const geometry = LineSegment2D.fromPointObjects(iJoint, jJoint);

        const memberType = memberTypeToLoadMemberType(memberStack.memberType, geometry, componentDesign.component.span);
        if (!memberType) return null;

        const isVertical = geometry.direction.isParallelTo(Direction2D.up);

        return { geometry, axis: isVertical ? 'Y' : 'X', memberType };
      })
      .filter(notNullish),
);

const getManualLoadSnapsFromComponent = createSelector(
  getActiveComponentDesign,
  (componentDesign): readonly ManualLoadSnap[] | null =>
    componentDesign &&
    componentDesign.component.members
      .map((member): ManualLoadSnap | null => {
        const midline = calculateMemberMidline(member, componentDesign.component);
        if (!midline) return null;
        const geometry = LineSegment2D.fromSegmentObject(midline).orientWith(Direction2D.right);

        const memberType = memberTypeToLoadMemberType(member.memberType, geometry, componentDesign.component.span);
        if (!memberType) return null;

        const isVertical = geometry.direction.isParallelTo(Direction2D.up);

        return { geometry, axis: isVertical ? 'Y' : 'X', memberType };
      })
      .filter(notNullish),
);

const getManualLoadSnaps = createSelector(
  [getManualLoadSnapsFromAnalogModel, getManualLoadSnapsFromComponent],
  (snapsFromAnalogModel, snapsFromComponent) => snapsFromAnalogModel ?? snapsFromComponent,
);

const getPersistentManualLoadVisuals = createSelector(
  [getActiveComponentDesign, getManualLoadSnaps, getLoadCasesForActiveComponentDesign],
  (componentDesign, manualLoadSnaps, loadCases) => {
    if (!componentDesign || !manualLoadSnaps || loadCases.length) return [];

    const manualLoadSettings = componentDesign.additionalLoads?.manualLoadSettings;
    if (!manualLoadSettings) return [];

    return manualLoadSettings
      .map((load) => manualLoadSettingToLoadVisualLoad(load, manualLoadSnaps))
      .filter(notNullish);
  },
);

export const getVisibleLoadCaseLoads = createSelector(
  getLoadCasesForActiveComponentDesign,
  (state: RootState) => state.visualOptions.visibleLoadGuids,
  (loadCasesForActiveComponentDesign, visibleLoadGuids): readonly LoadVisualLoad[] =>
    loadCasesForActiveComponentDesign.flatMap(({ loads }) => loads).filter((load) => visibleLoadGuids[load.guid]),
);

export const getVisibleLoads = createSelector(
  getVisibleLoadCaseLoads,
  getLoadCasesForActiveComponentDesign,
  getPersistentManualLoadVisuals,
  (state: RootState) => state.visualOptions.showManualLoadsByDefault,
  (
    loadCaseLoads,
    loadCasesForActiveComponentDesign,
    persistentManualLoads,
    showManualLoadsByDefault,
  ): readonly LoadVisualLoad[] => {
    if (loadCaseLoads.length || !showManualLoadsByDefault) return loadCaseLoads;
    const userLoads = getUserLoads(loadCasesForActiveComponentDesign);
    if (userLoads.length) return userLoads;
    return persistentManualLoads;
  },
);
